import React, { useRef, useEffect } from 'react'
import { useImperativeHandle, forwardRef } from 'react'
import { init, getInstanceByDom } from 'echarts'
import type { CSSProperties } from 'react'
import { EChartsOption, ECharts, SetOptionOpts, registerMap } from 'echarts'
import './themes/ta.js'
import './themes/ta-dark.js'
import { on } from 'events'

// SEE https://dev.to/manufac/using-apache-echarts-with-react-and-typescript-353k

export interface ReactEChartsProps {
  option: EChartsOption
  style?: CSSProperties
  mapImage?: string
  settings?: SetOptionOpts
  loading?: boolean
  theme?: string
  height?: string
  onClicked: (params: any) => void
}

export const ReactECharts = forwardRef(
  (
    {
      option,
      style,
      mapImage,
      settings,
      loading,
      theme,
      height,
      onClicked,
    }: ReactEChartsProps,
    ref
  ): JSX.Element => {
    const chartRef = useRef<HTMLDivElement>(null)

    useImperativeHandle(ref, () => ({
      closeTooltip: () => {
        if (chartRef.current !== null) {
          const chart = getInstanceByDom(chartRef.current)
          chart?.dispatchAction({
            type: 'hideTip',
          })
        }
      },
    }))

    useEffect(() => {
      // Initialize chart
      let chart: ECharts | undefined
      if (chartRef.current !== null) {
        chart = init(chartRef.current, theme, { renderer: 'svg' })
      }

      // Add chart resize listener
      // ResizeObserver is leading to a bit janky UX
      function resizeChart() {
        chart?.resize()
      }
      window.addEventListener('resize', resizeChart)

      // Return cleanup function
      return () => {
        chart?.dispose()
        window.removeEventListener('resize', resizeChart)
      }
    }, [theme])

    useEffect(() => {
      // Update chart
      if (chartRef.current !== null) {
        const chart = getInstanceByDom(chartRef.current)
        if (mapImage) {
          // fetch svg
          fetch(mapImage)
            .then((response) => response.text())
            .then((svg) => {
              registerMap('map_svg', { svg: svg })
              chart?.setOption(option, settings)
            })
        } else {
          chart?.setOption(option, settings)
        }
      }
    }, [option, settings, theme]) // Whenever theme changes we need to add option and setting due to it being deleted in cleanup function

    useEffect(() => {
      // Update chart
      if (chartRef.current !== null) {
        const chart = getInstanceByDom(chartRef.current)
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        loading === true ? chart?.showLoading() : chart?.hideLoading()
      }
    }, [loading, theme])

    return (
      <div
        onClick={onClicked}
        ref={chartRef}
        style={{
          verticalAlign: 'top',
          width: '100%',
          height: height ? height : '300px',
          ...style,
        }}
      />
    )
  }
)

export default ReactECharts
